import apiClient from "./index";
import { LoginResponse, User, Address } from "../types";
import { ApiError } from "../types/error";
import axios, { isAxiosError } from "axios";
import { setItem, getItem, removeItem } from "../utils/localStorage";
import { handleApiError } from "../utils/utils";

export interface ApiResponse<T> {
  data: T | null;
  error: ApiError | null;
}

export interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  dateOfBirth: string;
  address: Address;
  preferences: string[];
  allergies: string[];
  photo: File;
}

export interface UpdatePayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: Address;
  preferences: string[];
  allergies: string[];
  photo: File;
}

export const register = async (
  data: RegisterPayload
): Promise<ApiResponse<LoginResponse>> => {
  try {
    const formData = new FormData();
    const { photo, ...userData } = data;

    const userDataBlob = new Blob([JSON.stringify(userData)], {
      type: "application/json",
    });

    formData.append("customerReqDto", userDataBlob);
    formData.append("picture", photo);

    const response = await apiClient.post<LoginResponse>(
      "/customers",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    setItem("authToken", response.data.accessToken);
    setItem("refreshToken", response.data.refreshToken);
    setItem("userId", response.data.id);
    setItem("user", response.data);
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred during registration."
    );
  }
};

export interface LoginCredentials {
  email: string;
  password: string;
}

export const login = async (
  credentials: LoginCredentials
): Promise<ApiResponse<LoginResponse>> => {
  try {
    const response = await apiClient.post<LoginResponse>(
      "/auth/login",
      credentials,
      {
        headers: {
          "X-Request-Source": "user",
        },
      }
    );
    setItem("authToken", response.data.accessToken);
    setItem("refreshToken", response.data.refreshToken);
    setItem("userId", response.data.id);
    setItem("user", response.data);
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(error, "An unknown error occurred during login.");
  }
};

export const getProfile = async (
  userId: string
): Promise<ApiResponse<User>> => {
  try {
    const response = await apiClient.get<User>(`/customers/${userId}`);
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred while fetching profile."
    );
  }
};

export const updateProfile = async (
  data: Partial<UpdatePayload>
): Promise<ApiResponse<User>> => {
  try {
    const formData = new FormData();
    const { photo, ...userData } = data;

    const userDataBlob = new Blob([JSON.stringify(userData)], {
      type: "application/json",
    });

    formData.append("customerReqDto", userDataBlob);
    if (photo) {
      formData.append("picture", photo);
    }

    console.log("*****************************  form to send ", formData);

    const response = await apiClient.put<User>(`/customers`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred while updating profile."
    );
  }
};

export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export const changePassword = async (
  payload: ChangePasswordPayload
): Promise<ApiResponse<{}>> => {
  try {
    const response = await apiClient.post("/auth/change-password", payload);
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred while cganging password."
    );
  }
};

export interface ForgotPasswordPayload {
  email: string;
}

export const forgotPassword = async (
  payload: ForgotPasswordPayload
): Promise<ApiResponse<{}>> => {
  try {
    const response = await apiClient.post("/auth/forgot-password", payload, {
      headers: {
        "X-Request-Source": "user",
      },
    });
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred while reinitializing password."
    );
  }
};

export const logout = async (): Promise<ApiResponse<unknown>> => {
  try {
    const response = await apiClient.post("/auth/logout");
    removeItem("authToken");
    removeItem("refreshToken");
    removeItem("userId");
    removeItem("user");
    removeItem("currentOrderID");
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(error, "An unknown error occurred during logout.");
  }
};

export const refreshAccessToken = async (): Promise<
  ApiResponse<{ accessToken: string; refreshToken: string }>
> => {
  try {
    const refreshToken = getItem("refreshToken");
    console.log("refreshToken", refreshToken);
    const refreshInstance = axios.create({
      baseURL: apiClient.defaults.baseURL,
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    // Appel à l'API pour rafraîchir le jeton
    const refreshResponse = await refreshInstance.post("/auth/refresh-token");

    console.log("refreshResponse", refreshResponse);

    setItem("authToken", refreshResponse.data.accessToken);
    setItem("refreshToken", refreshResponse.data.refreshToken);
    return { data: refreshResponse.data, error: null };
  } catch (error) {
    return handleApiError(error, "An unknown error occurred during logout.");
  }
};

export const deleteAccount = async (
  userId: string
): Promise<ApiResponse<User>> => {
  try {
    const response = await apiClient.delete<User>(`/customers/${userId}`);
    removeItem("authToken");
    removeItem("refreshToken");
    removeItem("userId");
    removeItem("user");
    removeItem("currentOrderID");
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred while deleting account."
    );
  }
};
